<template>
    <div>
        <modal
            v-show="!showChild"
            class="ddModal"
            title="Dynamic Collection Options"
            header-bg-variant="blue-600"
            header-text-variant="white"
            :hide-header-close="true"
            @ok="onConfirm"
            @hide="onHide"
        >
            <div class="ddMapping" @click="onClickAway">
                <div class="ddMapping__breadcrumbs">
                    <template v-for="(item, index) in breadcrumbItems">
                        <span v-if="index" :key="index"> > </span>
                        <button
                            :key="`${item}-${index}`"
                            @click="onBreadcrumbClick(index)"
                        >
                            {{ item }}
                        </button>
                    </template>
                </div>
                <article class="ddMapping__row ddMapping__mainPanel" :style="mainPanelStyles">
                    <div class="ddMapping__panel">
                        <vue-custom-scrollbar
                            ref="componentColumn"
                            tagname="section"
                            class="ddMapping__column ddMapping__column--component"
                            :settings="scrollSettings"
                            style="height: 100%;"
                        >
                            <h3 class="ddMapping__heading">
                                Component
                            </h3>

                            <div class="ddMapping__section">
                                <h4 class="ddMapping__label">
                                    SELECT A COMPONENT
                                </h4>

                                <toolbar-insert
                                    mode="click"
                                    :toolbar="toolbarId"
                                    :screen="screen"
                                    :overflow-scroll="false"
                                />
                            </div>
                        </vue-custom-scrollbar>

                        <section v-show="selectedComponentId" class="ddMapping__column ddMapping__column--next">
                            <button
                                type="button"
                                class="ddMapping__next"
                                @click="paneIndex = 1"
                            >
                                <font-awesome-icon :icon="['fal', 'chevron-circle-right']" />

                                <span class="ddMapping__next-text">
                                    Set Mapping
                                </span>
                            </button>
                        </section>
                    </div>

                    <div class="ddMapping__panel">
                        <section class="ddMapping__column ddMapping__column--back">
                            <button
                                type="button"
                                class="ddMapping__back"
                                @click="paneIndex = 0"
                            >
                                <font-awesome-icon :icon="['fal', 'chevron-circle-left']" />
                            </button>
                        </section>

                        <section
                            ref="mappingColumn"
                            class="ddMapping__column ddMapping__column--mapping"
                            style="height: 100%;"
                        >
                            <div class="fc-display-contents">
                                <h3 class="ddMapping__heading">
                                    Data Mapping - {{ selectedComponentName }}
                                </h3>

                                <div
                                    v-show="!transformMode"
                                    class="ddMapping__section"
                                >
                                    <div class="ddMapping__field">
                                        <div class="ddMapping__field-column">
                                            <h4 class="ddMapping__label">
                                                COMPONENT FIELD
                                            </h4>
                                        </div>
                                    </div>
                                    <div class="ddMapping__field">
                                        <div class="ddMapping__field-column">
                                            <dropdown
                                                placeholder="Select One"
                                                :value="selectedAtomIndex"
                                                :options="atomCollectionOptions"
                                                :max-list-height="200"
                                                @input="onAtomSelect"
                                            />
                                        </div>
                                        <div class="ddMapping__field-column">
                                            <button
                                                class="ddMapping__button"
                                                :disabled="selectedAtomIndex < 0"
                                                @click="toggleTransformMode"
                                            >
                                                Add
                                            </button>
                                        </div>
                                    </div>
                                    <div class="ddMapping__field">
                                        <div class="ddMapping__field-column">
                                            <h4 class="ddMapping__label">
                                                ACTIVE MAPPINGS
                                            </h4>
                                        </div>
                                    </div>
                                    <div class="ddMapping__active-mappings">
                                        <span
                                            v-if="!atomCollectionTransforms.length"
                                            class="f-italic"
                                        >
                                            { empty }
                                        </span>
                                        <div v-else>
                                            <div
                                                v-for="(item, index) in atomCollectionTransforms"
                                                :key="index"
                                            >
                                                <button
                                                    class="ddMapping__button ddMapping__button--alt f-m-0.5 f-rounded-0.375 f-w-1/3"
                                                    @click="selectAtom(item)"
                                                >
                                                    {{ item.displayName }}
                                                </button>
                                                <button
                                                    class="ddMapping__button f-m-0.5"
                                                    @click="onMappingEdit(item)"
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    class="ddMapping__button f-m-0.5"
                                                    @click="onRemoveMapping(item)"
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div
                                    v-show="transformMode"
                                    class="ddMapping__section"
                                >
                                    <h4 class="ddMapping__label">
                                        TRANSFORM ({{ selectedAtomDisplayName }}) - use $data for the root item:
                                    </h4>
                                    <div class="ddMapping__transform">
                                        <rich-html-editor
                                            :id="richTextEditorId"
                                            ref="codeEditor"
                                            mode="javascript"
                                            theme="code"
                                            :input="editorCode"
                                            :auto-complete-words="autoCompleteWords"
                                            @change="onEditorCodeChange"
                                        />
                                    </div>
                                    <vue-custom-scrollbar
                                        tagname="section"
                                        class="ddMapping__sample"
                                        style="height: 28vh;"
                                        :settings="scrollSettings"
                                    >
                                        <json-clickable
                                            :data="truncatedDataObject"
                                        />
                                    </vue-custom-scrollbar>

                                    <div class="ddMapping__transform-footer">
                                        <button
                                            class="ddMapping__button ddMapping__button--alt"
                                            @click="toggleTransformMode"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            v-if="!selectedAtomIsDynamicCollection"
                                            class="ddMapping__button"
                                            @click="applyTransform"
                                        >
                                            Apply
                                        </button>
                                        <button
                                            v-else
                                            class="ddMapping__button"
                                            @click="openNested"
                                        >
                                            Map
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </section>
                    </div>
                </article>

                <div class="ddMapping__bar" :style="barStyles">
                    <button v-show="selectedComponentId" class="ddMapping__bar-handle" @click="onBarClick">
                        <font-awesome-icon :icon="['fas', barCaret]" />
                    </button>
                </div>

                <figure class="ddMapping__row ddMapping__preview" :style="previewPanelStyles">
                    <vue-custom-scrollbar
                        class="ddMapping__column ddMapping__column--preview"
                        :settings="scrollSettings"
                        style="height: 100%;"
                    >
                        <loading-component v-show="isLoading" />

                        <component
                            :is="selectedComponent.elementType"
                            v-if="selectedComponent && selectedComponent.elementType"
                            :key="componentUpdateTrigger"
                            class="ddMapping__preview-component"
                            :class="{ 'ddMapping--disable-pointer': paneIndex != 1 }"
                            :pointer="1"
                            :index="0"
                            v-bind="selectedComponent"
                        />
                    </vue-custom-scrollbar>
                </figure>
            </div>

            <template v-slot:modal-footer>
                <button class="flex-modal__button flex-modal__button--cancel" @click="onCancel">
                    Back
                </button>

                <button class="flex-modal__button flex-modal__button--continue" @click="onConfirm">
                    Update
                </button>
            </template>
        </modal>
        <modal-dynamic-options-nested
            v-if="showChild"
            :is-child="true"
            :component-id="childComponentId"
            :collection-data="childData"
            :dynamic-data-mapper="childMapping"
            :parent-breadcrumbs="breadcrumbItems"
            @child-update="onChildUpdated"
            @child-cancel="onChildCancel"
            @breadcrumb="onChildBreadcrumbClick"
        />
    </div>
</template>

<script>
import vueCustomScrollbar from 'vue-custom-scrollbar';
import ModalDynamicOptionsBase from '@/mixins/modal-dynamic-options-base';

import LoadingComponent from '@/components/shared/loading/loading-component';
import ToolbarInsert from '@/components/shared/toolbars/toolbar-insert';
import RichHtmlEditor from '@/components/shared/modal/rich-html-editor';
import JsonClickable from '@/components/utilities/json-clickable';

export default {
    name: 'modal-dynamic-options-nested',
    components: {
        LoadingComponent,
        ToolbarInsert,
        vueCustomScrollbar,
        RichHtmlEditor,
        JsonClickable,
    },

    mixins: [ModalDynamicOptionsBase],

    props: {
        collectionData: {
            type: [Object, String, Number],
            default: null,
        },

        componentId: {
            type: String,
            default: '',
        },

        dynamicDataMapper: {
            type: Array,
            default: null,
        },

        parentBreadcrumbs: {
            type: Array,
            default: null,
        },
    },

    computed: {
        // styles - BEGIN
        mainPanelStyles() {
            let transform;
            let width = '100%';

            if (this.selectedComponentId) {
                width = this.isPreviewExpanded
                    ? `${ 100 - this.previewWidth.expanded }%`
                    : `${ 100 - this.previewWidth.collapsed }%`;
            }

            switch (this.paneIndex) {
                case 0:
                    transform = 'translateX(0%)';
                    break;

                case 1:
                    transform = 'translateX(-100%)';
                    break;

                default:
                    console.warn(`Cannot switch to pane index ${ this.paneIndex }, as it does not exist.`);
            }

            return {
                width,
                transform,
            };
        },

        // styles - END
    },

    watch: {
        paneIndex() {
            this.$refs.componentColumn.$el.scrollTo(0, 0);
            this.$refs.mappingColumn.scrollTo(0, 0);
        },
    },

    created() {
        this.init();
        this.listenOnRoot('flex::json::click', this.insertIntoEditorCode);
        this.listenOnRoot('flex::dynamicMap::click', this.handlePreviewAtomClick);
        this.listenOnRoot('flex::dynamicMap::doubleClick', this.handlePreviewAtomDoubleClick);
        this.breadcrumbItems = this.parentBreadcrumbs.concat(new Array('current'));
    },

    methods: {
        init() {
            this.listenOnRoot('flex::component::select', this.onComponentSelect);

            this.processNewObject();
            this.loadComponent();
        },

        onClickAway() {
            // close the dropdowns
            this.$root.$emit('flex::dropdown::close');
        },

        onConfirm() {
            this.$emit('child-update', {
                selectedPremadeComponentId: this.selectedComponentId,
                dynamicDataMapper: this.atomCollection,
            });
        },

        onCancel() {
            this.$emit('child-cancel');
        },

        hideModalBase() {
            this.hideModal();
            this.paneIndex = 0;
        },

        processNewObject() {
            this.objectProperties = [];
            this.selectedComponentId = this.componentId;
            this.dataObject = typeof this.collectionData === 'object'
                ? { ...this.collectionData }
                : this.collectionData;

            if (this.dynamicDataMapper) {
                this.atomCollection = this.dynamicDataMapper;
            }
        },
    },
};
</script>

<style lang="scss">
.ddMapping {
    &__breadcrumbs {
        padding: .5em 4em 0;

        > button:not(:last-of-type) {
            text-decoration: underline;
        }
    }
}
</style>
